import { useEffect, useState } from 'react';
import { Table, Button } from 'antd';
import { Chip, Typography } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Swal from 'sweetalert2';
import axios from 'axios';
import SectionCard from './SectionCard';
import Data from '../../../Data/Data.json';
import { Crypter } from '../../../Functions/Security/Crypter';
import type { CompteStatutRow } from '../../../Models/DemandeKyc';

interface KycComptesPanelProps {
  numcli: string;
  numcpt: string;
  onCompteChange: (numcpt: string, compteInfo: any | null) => void;
}

// Onglet "Compte" : liste des comptes du client (chargée dès qu'un client est
// choisi dans l'onglet Demande) et sélection d'un compte à modifier.
export default function KycComptesPanel({ numcli, numcpt, onCompteChange }: KycComptesPanelProps) {
  const [comptesClient, setComptesClient] = useState<CompteStatutRow[]>([]);
  const [loadingComptes, setLoadingComptes] = useState(false);

  // Utilise le mécanisme générique AnySearch (celui qui alimente tous les
  // picklists "N° compte" de l'appli). CodeAgence/Colagence restent vides :
  // sp_AfficheResultPiclist les utilise pour filtrer par agence, sans rapport
  // avec le client — seul colfiltre/valfiltre (NUMCLI) doit filtrer ici.
  async function consultComptesClient(currentNumcli: string) {
    setLoadingComptes(true);
    try {
      const body = {
        CodeAgence: '', Table: 'COMPTES', Colonne1: 'NUMCPT', Colonne2: 'NOMCPT',
        Colagence: '', Init: '', colfiltre: 'NUMCLI', valfiltre: currentNumcli, choix: 22,
      };
      const response = await axios.get(Data.ApiUrl.AnySearch, {
        headers: { CypherSearch: Crypter.encrypt(JSON.stringify(body)), 'Content-Type': 'application/json' },
        withCredentials: true,
      });
      const result = JSON.parse(Crypter.decrypt(response.data)).Value;
      const rows = result?.StatutCode === 200 ? (result.Result?.Table ?? []) : [];
      const comptesBase: CompteStatutRow[] = rows.map((row: any) => {
        const keys = Object.keys(row);
        return { NUMCPT: row[keys[1]], NOMCPT: row[keys[0]] };
      });
      setComptesClient(comptesBase);

      // Le plan comptable (IDPLAN/lib_IDPLAN) n'est pas renvoyé par ce
      // picklist générique (2 colonnes seulement, code+intitulé) — un
      // GetCompte par compte (peu nombreux pour un client) complète la
      // colonne "Plan comptable" juste après "Intitulé".
      const comptesEnrichis = await Promise.all(comptesBase.map(async (compte) => {
        try {
          const detailResponse = await axios.get(Data.ApiUrl.GetCompte, {
            headers: { CypherNumCpte: Crypter.encrypt(compte.NUMCPT), 'Content-Type': 'application/json' },
            withCredentials: true,
          });
          const detail = JSON.parse(Crypter.decrypt(detailResponse.data));
          const info = detail?.Result?.Table?.[0];
          return { ...compte, IDPLAN: info?.IDPLAN, lib_IDPLAN: info?.lib_IDPLAN };
        } catch {
          return compte;
        }
      }));
      setComptesClient(comptesEnrichis);
    } catch (error: any) {
      setComptesClient([]);
      if (error?.response?.status !== 404) {
        Swal.fire({ title: 'Erreur', text: 'Impossible de récupérer les comptes du client.', icon: 'error' });
      }
    } finally {
      setLoadingComptes(false);
    }
  }

  useEffect(() => {
    if (numcli) consultComptesClient(numcli);
    else setComptesClient([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numcli]);

  async function selectCompte(row: CompteStatutRow) {
    try {
      const response = await axios.get(Data.ApiUrl.GetCompte, {
        headers: { CypherNumCpte: Crypter.encrypt(row.NUMCPT), 'Content-Type': 'application/json' },
        withCredentials: true,
      });
      const res = JSON.parse(Crypter.decrypt(response.data));
      const info = res?.Result?.Table?.[0] ?? null;
      onCompteChange(row.NUMCPT, info);
    } catch {
      onCompteChange(row.NUMCPT, null);
    }
  }

  if (!numcli) {
    return <Typography color="text.secondary" sx={{ p: 2 }}>Sélectionnez un client dans l'onglet Demande pour voir ses comptes.</Typography>;
  }

  return (
    <SectionCard icon={<AccountBalanceWalletIcon fontSize="small" />} title="Comptes du client" color="#0277bd">
      <Table
        rowKey="NUMCPT" size="small" pagination={false} bordered
        loading={loadingComptes}
        dataSource={comptesClient}
        locale={{ emptyText: 'Aucun compte trouvé pour ce client.' }}
        columns={[
          { title: 'N° compte', dataIndex: 'NUMCPT', key: 'NUMCPT' },
          { title: 'Intitulé', dataIndex: 'NOMCPT', key: 'NOMCPT' },
          {
            title: 'Plan comptable', key: 'IDPLAN',
            render: (_: any, row: CompteStatutRow) => (
              row.IDPLAN && row.lib_IDPLAN ? `${row.IDPLAN} — ${row.lib_IDPLAN}` : (row.lib_IDPLAN || row.IDPLAN || '—')
            ),
          },
          {
            title: 'Action', key: 'action', align: 'right' as const,
            render: (_: any, row: CompteStatutRow) => (
              row.NUMCPT === numcpt ? (
                <Chip size="small" color="success" icon={<CheckCircleIcon style={{ fontSize: 16 }} />} label="Sélectionné" />
              ) : (
                <Button size="small" className="bt_add" style={{ color: '#fff' }} onClick={() => selectCompte(row)}>
                  Sélectionner
                </Button>
              )
            ),
          },
        ] as any}
      />
    </SectionCard>
  );
}
